import { SendRounded } from "@mui/icons-material";
import { alpha, Avatar, AvatarGroup, Box, Container, IconButton, List, ListItem, ListItemAvatar, ListItemText, Paper, Skeleton, Stack, styled, Typography } from "@mui/material";
import { collection, getFirestore, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../store";
import { navigate } from "../../store/slices/breadcrumbSlice";
import { Employee } from "../../types/Employee";
import { Message } from "../../types/Message";
import { MessageThread, MessageThreadMember } from '../../types/MessageThread';
import { queryEmployee } from "../../utils/queryEmployee";
import { queryMessageThread } from "../../utils/queryMessages";
import { createMessageThread, sendMessage } from "../../utils/sendMessage";
import { messageConverter } from "../../converters/messageConverter";

const CleanInput = styled('input')(({ theme }) => ({
  ...theme.typography.body1,
  backgroundColor: 'transparent',
  border: 'none',
  color: theme.palette.text.primary,
  outline: 'none',
}));

export const ConversationBox = () => {
  const me = useAppSelector((s) => s.auth) as unknown as Employee;
  const { threadId } = useParams();

  const dummyRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState<MessageThread>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState('');

  const handleSendOnClick = () => {
    
  };

  useEffect(() => {
    dummyRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setLoading(true);
    setThread(undefined);
    setMessages([]);

    if (threadId) {
      const unsubscribe = queryMessageThread(me.idNumber, threadId).then(async (messageThread) => {
        if (messageThread) {
          setThread(messageThread);
          setLoading(false);

          return onSnapshot(query(
            collection(
              getFirestore(), `Employees/${me.idNumber}/Messages/${messageThread.id}/Conversations`),
              orderBy('dateSent', 'desc'),
              limit(25)
            ).withConverter(messageConverter), async (snapshot) => {
            const docChanges = snapshot.docChanges();

            const newMessages: any[] = [];
            for (let i = 0; i < docChanges.length; i++) {
              const change = docChanges[i];
              const message = change.doc.data();

              // Get sender details
              let sender: Employee | undefined = me
              if (message.senderId != me.idNumber) 
                sender = messageThread.members.find(v => v.idNumber === message.senderId)?.employee
              if (sender)
                message.sender = sender

              if (change.type === 'added') {
                newMessages.push(message);
              }
            }

            setMessages(prev => [...prev, ...newMessages.reverse()]);
          });
        }

        setThread(messageThread);
        setLoading(false);
        return () => {};
      });

      return () => {
        unsubscribe.then((u) => {
          u();
        });
      };
    } else {
      setLoading(false);
    }
  }, [threadId]);

  return (
    <Container disableGutters sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <Stack component={Paper} direction="row" spacing={1} sx={{ padding: 1, alignItems: 'center', height: 56 }}>
        {loading ? (
          <>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" sx={{ width: 300 }} />
          </>
        ) : (
          <>
            <AvatarGroup max={3}>
              {thread?.members?.filter((m) => m.idNumber !== me.idNumber).map((m) => {
                if (m.employee === undefined) {
                  return (
                    <Avatar>A</Avatar>
                  )
                } else {
                  return (
                    <Avatar key={m.employee.uid} src={m.employee.picture} alt={m.employee.name} />
                  );
                }
              })}
            </AvatarGroup>
            {thread?.members.filter((m) => m.idNumber !== me.idNumber).map((m) => {
              return (
                <Typography key={m.employee?.uid ?? m.idNumber}>{m.employee?.name ?? m.idNumber}</Typography>
              );
            })}
          </>
        )}
      </Stack>
      <Stack sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Stack sx={{ paddingY: 2 }}>
          {messages.map((m) => {
            return (
              <Stack 
                key={m.id} 
                direction={m.senderId === me.idNumber ? 'row-reverse' : 'row'} 
                spacing={2}
                sx={{
                  padding: 1,
                  paddingY: 0.25,
                }}
              >
                {m.senderId !== me.idNumber && m.sender !== undefined && (
                  <Avatar src={m.sender.picture} alt={m.sender.name} sx={{ width: 24, height: 24 }} />
                )}
                <Box>
                  <Box
                    sx={{
                      backgroundColor: theme => 
                        m.senderId === me.idNumber 
                          ? alpha(theme.palette.mode === 'dark' ? '#ffffff' : '#000000', 0.1) 
                          : theme.palette.primary.main,
                      borderRadius: 4,
                      padding: 1,
                      paddingY: 0.5,
                    }}
                  >
                    <Typography 
                      sx={{ color: theme => 
                        m.senderId === me.idNumber 
                        ? theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black
                        : theme.palette.common.white 
                    }}>
                      {m.body}
                    </Typography>
                  </Box>
                  <span style={{ fontSize: '11px' }}>
                    {m.dateSent.toLocaleString(DateTime.DATETIME_SHORT)}
                  </span>
                </Box>
              </Stack>
            );
          })}
          <div ref={dummyRef} />
        </Stack>
      </Stack>
      <Stack component={Paper} direction="row" sx={{ padding: 1 }}>
        <CleanInput sx={{ flexGrow: 1, }} value={message} onChange={(e) => setMessage(e.target.value)} />
        <IconButton color="primary" onClick={handleSendOnClick} disabled={message.trim() === ''}>
          <SendRounded />
        </IconButton>
      </Stack>
    </Container>
  );
};